import { memo, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { CheckCircle as CheckIcon } from "@mui/icons-material";
import { green, grey } from "@mui/material/colors";

import UserSelectionPrompt from "./UserSelectionPrompt";
import UserFileUploadPrompt from "./UserFileUploadPrompt";
import UserProfileFormPrompt from "./UserProfileFormPrompt";
import WebSearchApproval from "./WebSearchApproval";
import PlanTemplatePublishApproval from "./PlanTemplatePublishApproval";
import FileDownload from "./FileDownload";
import SearchResultsDisplay from "./SearchResultsDisplay";
import EmailStagedPreview from "./EmailStagedPreview";
import CrDraftCreatedCard, { selectCrDraftResult } from "./CrDraftCreatedCard";
import {
  deriveCompletionMessage,
  getInnerResult,
  isToolResponseTextComplete,
  parseToolResponseText,
} from "./toolStatusUtils";
import { useChatContext } from "../../context/ChatContext";
import {
  isFfnRecordDetail,
  useFfnRecordStore,
} from "src/views/assistant/stores/FfnRecordStore";
import {
  useChatStreamStore,
  useChatStreamValue,
} from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";

// Re-exported from toolStatusUtils (moved there so CompactToolBlock's
// helpers can share it without a circular import).
export { getInnerResult };

const EMPTY_ARTIFACTS = {};

interface ToolResponseProps {
  text: string;
  partIndex: number;
  messageId?: string | null;
  parentMessageId?: string | null;
  isChatConnected?: boolean;
}

/**
 * ToolResponse renders the result portion of a tool usage block.
 * Parses the tool response JSON and displays it via InlineToolOutput
 * (which pairs visually with ArtifactCard rendered by ToolCall above).
 *
 * Special case: UserSelectionPrompt responses render an interactive UI
 * instead of the standard inline output.
 */
const ToolResponse = memo(
  ({
    text,
    partIndex,
    messageId,
    parentMessageId,
    isChatConnected = false,
  }: ToolResponseProps) => {
    const { dialogueId, compactToolCalls } = useChatContext();
    // Read-only host-store access — useAtomAssistChat would setStreamConfig
    // its atom-assist endpoint and handlers onto the host surface's store.
    const streamStore = useChatStreamStore();
    const artifacts = useChatStreamValue((s) => s.artifacts, EMPTY_ARTIFACTS);
    const artifactsLength = Object.keys(artifacts).length;
    const processedRef = useRef<string>("");

    const isComplete = useMemo(
      () => isToolResponseTextComplete(text, isChatConnected),
      [text, isChatConnected],
    );

    const responseContent = useMemo(
      () => (isComplete ? parseToolResponseText(text) : null),
      [text, isComplete],
    );

    // Populate the artifacts store so watchers (e.g. ProposalGenerationReportPanel,
    // PartsAISidebar) can detect tool completions during streaming.
    useEffect(() => {
      if (!isComplete || !responseContent || !messageId || !streamStore) {
        return;
      }

      // Extract tool_name injected by backend via _inject_tool_call_info
      const toolName: string | undefined =
        typeof responseContent === "object" && responseContent !== null
          ? responseContent.tool_name
          : undefined;

      if (!toolName) return;

      // Use TOOL_RESPONSE's own partIndex for the artifact ID.
      const artifactId = `artifact-${messageId}-${toolName}-${partIndex}`;

      // Skip if we already processed this exact artifact
      if (processedRef.current === artifactId) return;

      // Check if an artifact with this exact ID already exists
      // (e.g. created by reconstructArtifactsFromMessages on history load)
      if (artifactId in artifacts) {
        processedRef.current = artifactId;
        return;
      }

      processedRef.current = artifactId;

      const completionMsg = deriveCompletionMessage(responseContent);

      streamStore.getState().addArtifact({
        id: artifactId,
        messageId,
        parentId: parentMessageId ?? "",
        toolName,
        type: toolName,
        isLoading: false,
        content: { text: responseContent },
        statusMessage: completionMsg,
      });
    }, [
      isComplete,
      responseContent,
      messageId,
      parentMessageId,
      partIndex,
      streamStore,
      artifactsLength,
      isChatConnected,
    ]);

    // Mirror 50.59 record tool responses into the record side panel store.
    // Fires for both live-streamed and history-loaded responses, so the
    // panel state replays with the dialogue.
    useEffect(() => {
      if (!isComplete || dialogueId === undefined) return;
      const innerResult = getInnerResult(responseContent);
      if (!isFfnRecordDetail(innerResult)) return;
      useFfnRecordStore.getState().setRecord(dialogueId, innerResult);
    }, [isComplete, responseContent, dialogueId]);

    const isUserSelectionPrompt = useMemo(() => {
      const innerResult = getInnerResult(responseContent);
      if (!innerResult || "error" in innerResult) return false;
      return innerResult.type === "user_selection_prompt";
    }, [responseContent]);

    const isUserFileUploadPrompt = useMemo(() => {
      const innerResult = getInnerResult(responseContent);
      if (!innerResult || "error" in innerResult) return false;
      return innerResult.type === "user_file_upload_prompt";
    }, [responseContent]);

    // Check if this is an inline profile form (FTUX module 1)
    const isUserProfileForm = useMemo(() => {
      if (!responseContent || typeof responseContent !== "object") return false;
      if ("error" in responseContent) return false;
      return responseContent.type === "user_profile_form";
    }, [responseContent]);

    // Check if this is a web search approval prompt
    const isWebSearchApproval = useMemo(() => {
      if (!responseContent || typeof responseContent !== "object") {
        return false;
      }

      // Check for error responses
      if ("error" in responseContent) {
        return false;
      }

      const innerResult = getInnerResult(responseContent);
      if (!innerResult) return false;

      // Check for error in inner result
      if ("error" in innerResult) {
        return false;
      }

      return innerResult.type === "web_search_approval";
    }, [responseContent]);

    // Check if this is a plan-template publish approval prompt
    const isPlanTemplatePublishApproval = useMemo(() => {
      if (!responseContent || typeof responseContent !== "object") {
        return false;
      }
      if ("error" in responseContent) {
        return false;
      }
      const innerResult = getInnerResult(responseContent);
      if (!innerResult || "error" in innerResult) return false;
      return innerResult.type === "plan_template_publish_approval";
    }, [responseContent]);

    // Check if this is a file download result
    const fileDownloadData = useMemo(() => {
      if (!responseContent || typeof responseContent !== "object") {
        return null;
      }

      const innerResult = getInnerResult(responseContent);
      if (!innerResult || innerResult.type !== "file_download") return null;

      return {
        artifactId: innerResult.artifact_id,
        filename: innerResult.filename,
        fileSize: innerResult.file_size,
      };
    }, [responseContent]);

    // Check if this is an agent-authored Condition Report draft
    const crDraftData = useMemo(
      () => selectCrDraftResult(responseContent),
      [responseContent],
    );

    // Check if this is a search results display
    const searchResultsData = useMemo(() => {
      if (!responseContent || typeof responseContent !== "object") {
        return null;
      }

      const innerResult = getInnerResult(responseContent);
      if (!innerResult || innerResult.type !== "search_results_display")
        return null;

      return {
        query: innerResult.query || "",
        title: innerResult.title || "Search Results",
        results: innerResult.results || [],
        totalResults: innerResult.total_results || 0,
      };
    }, [responseContent]);

    // Check if this is a staged-email (Send Email tool) result. It has no
    // `type` discriminator, so key on its distinctive fields.
    const stagedEmailData = useMemo(() => {
      if (!responseContent || typeof responseContent !== "object") {
        return null;
      }
      const innerResult = getInnerResult(responseContent);
      // Key on email_log_id (distinctive to a staged email) plus a recipient
      // or subject. approve_url is NOT required — the card still renders
      // (just without the button) if the backend omits it.
      if (
        !innerResult ||
        innerResult.email_log_id == null ||
        (typeof innerResult.to !== "string" &&
          typeof innerResult.subject !== "string")
      ) {
        return null;
      }
      return {
        statusSummary: innerResult.status_summary,
        to: innerResult.to,
        subject: innerResult.subject,
        message: innerResult.message,
        approveUrl: innerResult.approve_url,
      };
    }, [responseContent]);

    // Render the staged-email preview card if applicable
    if (stagedEmailData && isComplete) {
      return (
        <EmailStagedPreview
          statusSummary={stagedEmailData.statusSummary}
          to={stagedEmailData.to}
          subject={stagedEmailData.subject}
          message={stagedEmailData.message}
          approveUrl={stagedEmailData.approveUrl}
        />
      );
    }

    // Render search results display if applicable
    if (searchResultsData && isComplete) {
      return (
        <SearchResultsDisplay
          query={searchResultsData.query}
          title={searchResultsData.title}
          results={searchResultsData.results}
          totalResults={searchResultsData.totalResults}
        />
      );
    }

    // Render the Condition Report draft hand-off card if applicable
    if (crDraftData && isComplete) {
      return <CrDraftCreatedCard result={crDraftData} />;
    }

    // Render file download button if applicable
    if (fileDownloadData && isComplete) {
      return (
        <FileDownload
          artifactId={fileDownloadData.artifactId}
          filename={fileDownloadData.filename}
          fileSize={fileDownloadData.fileSize}
          messageId={messageId ?? undefined}
        />
      );
    }

    // Render web search approval prompt if applicable
    if (isWebSearchApproval && isComplete) {
      return (
        <WebSearchApproval
          text={text}
          isChatConnected={isChatConnected}
          messageId={messageId ?? undefined}
          partIndex={partIndex}
        />
      );
    }

    // Render plan-template publish approval prompt if applicable
    if (isPlanTemplatePublishApproval && isComplete) {
      return (
        <PlanTemplatePublishApproval
          text={text}
          isChatConnected={Boolean(isChatConnected)}
          messageId={messageId ?? undefined}
          partIndex={partIndex}
        />
      );
    }

    // Render user selection prompt if applicable
    if (isUserSelectionPrompt && isComplete) {
      return (
        <UserSelectionPrompt
          text={text}
          isChatConnected={isChatConnected}
          messageId={messageId ?? undefined}
          partIndex={partIndex}
        />
      );
    }

    // Render mid-chat file upload prompt if applicable
    if (isUserFileUploadPrompt && isComplete) {
      return (
        <UserFileUploadPrompt
          text={text}
          isChatConnected={Boolean(isChatConnected)}
          messageId={messageId ?? undefined}
          partIndex={partIndex}
        />
      );
    }

    // Render inline profile form (FTUX module 1)
    if (isUserProfileForm && isComplete) {
      return (
        <UserProfileFormPrompt
          text={text}
          isChatConnected={Boolean(isChatConnected)}
          messageId={messageId ?? undefined}
          partIndex={partIndex}
        />
      );
    }

    // Hide responses from plan management tools - the plan tracker shows state
    const isPlanManagementResponse = (() => {
      if (!responseContent || typeof responseContent !== "object") return false;
      const r =
        responseContent.message_type === "tool_result"
          ? responseContent.result
          : responseContent;
      if (!r || typeof r !== "object") return false;
      // update_plan_step and add_plan_step responses have step_id + success
      return "step_id" in r && "success" in r;
    })();

    if (isPlanManagementResponse) return null;

    // Compact mode: the paired ToolCall's CompactToolBlock already shows the
    // result summary on its ⎿ line — no standalone completion footer.
    if (compactToolCalls) return null;

    const completionStatusMessage = deriveCompletionMessage(responseContent);
    if (!completionStatusMessage) return null;

    // Render a concise natural-language completion line (no raw JSON).
    return (
      <Box
        sx={{
          mt: 0,
          mb: 1,
          borderRadius: "0 0 6px 6px",
          border: `1px solid ${grey[300]}`,
          borderTop: "none",
          bgcolor: grey[100],
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.6,
            borderTop: `1px solid ${grey[300]}`,
            bgcolor: grey[50],
          }}
        >
          <CheckIcon sx={{ fontSize: 14, color: green[500] }} />
          <Typography
            variant="caption"
            sx={{
              color: grey[700],
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {completionStatusMessage}
          </Typography>
        </Box>
      </Box>
    );
  },
);

export default ToolResponse;
